import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Menu, ChevronDown, ChevronUp, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { apiFetch } from "../../services/api/Navbar";

/* ================= Interfaces ================= */
interface MenuItem {
  id?: number;
  title?: string;
  label?: string;
  url?: string;
  path?: string;
  children?: MenuItem[];
}

/* ================= Static Items ================= */
const STATIC_ITEMS: MenuItem[] = [{ label: "HOME", path: "/" }];

/* ================= Helper: Backend → Frontend ================= */
const mapBackendMenu = (items: any[]): MenuItem[] => {
  return items.map((item) => ({
    id: item.id,
    title: item.title,
    label: item.title,
    url: item.url || "#",
    children: item.children ? mapBackendMenu(item.children) : [],
  }));
};

/* ================= Component ================= */
export function Header() {
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [logo, setLogo] = useState<string>("");

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState<number | null>(
    null
  );
  const [mobileSubSubmenuOpen, setMobileSubSubmenuOpen] = useState<
    number | null
  >(null);

  /* ================= Fetch Menu ================= */
  useEffect(() => {
    const fetchMenu = async () => {
      setLoading(true);
      try {
        const res = await apiFetch("menu");
        const backendItems = res?.data?.data?.items || [];
        const dynamicMenu = mapBackendMenu(backendItems);
        /* 👉 YE ADD KARO */
        const backendLogo = res?.data?.data?.settings?.logo;
        if (backendLogo) {
          setLogo(backendLogo);
        }
        setMenuItems([...STATIC_ITEMS, ...dynamicMenu]);
      } catch (err) {
        console.error("Menu fetch error", err);
        setMenuItems(STATIC_ITEMS);
      } finally {
        setLoading(false);
      }
    };

    fetchMenu();
  }, []);

  /* ================= Contact Scroll ================= */
  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const footer = document.querySelector("footer");
    footer?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header className="bg-white shadow-sm border-b">
      <div className="min-w-full mx-auto px-2 lg:px-4">
        <div className="flex justify-around items-center h-28">
          {/* Logo */}
          <Link to="/" className="r">
            <img
              src={logo || "/images/logo.png"}
              alt="HOA"
              className="h-24 w-24 object-contain"
            />
          </Link>

          {/* ================= Desktop Menu ================= */}
          <nav className="hidden lg:flex flex-1 justify-center gap-2">
            {loading ? (
              <span className="text-gray-400">Loading...</span>
            ) : (
              menuItems.map((item) => (
                <div key={item.id || item.label} className="relative group">
                  <Link
                    to={item.path || item.url || "#"}
                    className="font-bold text-base text-gray-700 flex items-center uppercase hover:text-blue-600"
                  >
                    {item.title || item.label}
                    {item.children?.length ? (
                      <ChevronDown className="w-5 h-5 transition-transform duration-300 group-hover:rotate-180" />
                    ) : null}
                  </Link>

                  {/* Level 1 */}
                  {item.children?.length ? (
                    <div
                      className="absolute left-0 mt-[16px]  w-52 bg-white shadow-lg rounded-md z-50
                      transition-all duration-300 opacity-0 invisible group-hover:visible group-hover:opacity-100"
                    >
                      {item.children.map((sub) => (
                        <div
                          key={sub.id || sub.label}
                          className="relative group/sub"
                        >
                          <Link
                            to={sub.url || "#"}
                            className="flex justify-between px-4 py-2 font-bold text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                          >
                            {sub.title}
                            {sub.children?.length ? (
                              <ChevronRight className="w-5 h-5 transition-transform duration-300" />
                            ) : null}
                          </Link>

                          {/* Level 2 */}
                          {sub.children?.length ? (
                            <div className="absolute left-full top-0 w-52 bg-white shadow-lg rounded-md opacity-0 invisible group-hover/sub:visible group-hover/sub:opacity-100 transition duration-200">
                              {sub.children.map((third) => (
                                <Link
                                  key={third.id || third.label}
                                  to={third.url || "#"}
                                  className="block px-4 py-2 font-bold text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                                >
                                  {third.title}
                                </Link>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))
            )}
          </nav>

          {/* ================= Right Side ================= */}
          <div className="flex items-center gap-4">
            <Link to="/athletes/register" className="hidden lg:block">
              <button className="px-4 py-2 bg-gradient-to-r from-blue-900 via-indigo-900 to-blue-700 text-white rounded-md font-semibold hover:bg-blue-700">
                Register Now
              </button>
            </Link>

            {/* Mobile Toggle */}
            <Button
              variant="ghost"
              className="lg:hidden text-xl"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <Menu />
            </Button>
          </div>
        </div>

        {/* ================= Mobile Menu ================= */}

        {mobileMenuOpen && (
          <div className="lg:hidden border-t bg-white">
            {menuItems.map((item, idx) => {
              const hasChildren = item.children && item.children.length > 0;

              return (
                <div key={idx} className="border-b">
                  {/* LEVEL 1 */}
                  {hasChildren ? (
                    <button
                      className="w-full px-4 py-3 font-bold flex justify-between items-center"
                      onClick={() =>
                        setMobileSubmenuOpen(
                          mobileSubmenuOpen === idx ? null : idx
                        )
                      }
                    >
                      {item.title || item.label}
                      {mobileSubmenuOpen === idx ? (
                        <ChevronUp />
                      ) : (
                        <ChevronDown />
                      )}
                    </button>
                  ) : (
                    <Link
                      to={item.path || item.url || "#"}
                      className="block px-4 py-3 font-bold"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.title || item.label}
                    </Link>
                  )}

                  {/* LEVEL 2 */}
                  {mobileSubmenuOpen === idx &&
                    item.children?.map((sub, sIdx) => {
                      const hasSubChildren =
                        sub.children && sub.children.length > 0;

                      return (
                        <div key={sIdx} className="bg-gray-50">
                          {hasSubChildren ? (
                            <button
                              className="w-full px-6 py-2 font-bold flex justify-between items-center"
                              onClick={() =>
                                setMobileSubSubmenuOpen(
                                  mobileSubSubmenuOpen === sIdx ? null : sIdx
                                )
                              }
                            >
                              {sub.title}
                              <ChevronRight />
                            </button>
                          ) : (
                            <Link
                              to={sub.url || "#"}
                              className="block px-6 py-2 font-bold"
                              onClick={() => setMobileMenuOpen(false)}
                            >
                              {sub.title}
                            </Link>
                          )}

                          {/* LEVEL 3 */}
                          {mobileSubSubmenuOpen === sIdx &&
                            sub.children?.map((third) => (
                              <Link
                                key={third.id}
                                to={third.url || "#"}
                                className="block px-10 py-2 font-bold text-sm"
                                onClick={() => setMobileMenuOpen(false)}
                              >
                                {third.title}
                              </Link>
                            ))}
                        </div>
                      );
                    })}
                </div>
              );
            })}

            <Link
              to="/athletes/register"
              onClick={() => setMobileMenuOpen(false)}
            >
              <button className="w-full my-4 px-4 py-2 bg-gradient-to-r from-blue-900 via-indigo-900 to-blue-700 text-white rounded-md font-semibold">
                Register Now
              </button>
            </Link>
          </div>
        )}
      </div>
    </header>
  );
}
